import { Button, Table, TableBody } from '@mui/material';
import { useCallback, useState } from 'react';
import { Box } from '@mui/system';
import { ContactChatModal } from '@modules/ContactManager/components/Header/components/ContactChat/ContactChatModal';
import {
  CoApplicantName,
  CoApplicantTableCell,
  CoApplicantTableRow,
  LinkButton,
  PhoneWrapper,
} from '@styles/ContactManager/ContactStyles';

import { SectionTitle } from '@shared/components/SectionTitle';
import { GridCell } from '@shared/components/grid/GridCell';
import { Modal } from '@shared/components/modals/Modal';
import { formatPercentage } from '@shared/commonFunctions';
import { EBureauTypeString, ICreditScoreResponse } from '@modules/ContactManager/components/Header/components/CreditReport/interfaces/interfaces';
import { NContact } from '../validation';
import { IContactDetails } from './OptOutModal/interfaces';

interface ICoApplicantsProps {
  data: NContact.CoApplicant[];
  contactId?: string;
  onRemove: (id: string) => void;
  onEdit?: (coApplicantId: string) => void;
  setCoApplicantModalPosition?: React.Dispatch<React.SetStateAction<number>>;
  jointDtiResponse?: IJointGTIResponse;
}

export interface IJointGTI {
  contactIds?: string[];
  reportDTIs?: Record<EBureauTypeString, ICreditScoreResponse[]>;
  jointDTI?: number;
}

export interface IJointGTIResponse {
  jointGTI?: IJointGTI;
  isError: boolean;
  errorDescription?: string;
}

export const CoApplicants = ({ data, contactId, onRemove, setCoApplicantModalPosition, onEdit, jointDtiResponse }: ICoApplicantsProps) => {
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [contactDetails, setContactDetails] = useState<IContactDetails>({});
  const [contactIdFromRow, setContactIdFromRow] = useState(contactId ?? '');
  const [resetPosition, setResetPositon] = useState(0);

  const handleClick = useCallback((e: React.MouseEvent<HTMLSpanElement>, row: NContact.CoApplicant) => {
    setResetPositon((prevState) => prevState + 1);
    setPhoneNumber(existingPhoneNumber(row) as string);
    setContactDetails(
      {  
        firstName:row?.firstName??'',
        lastName:row?.lastName??'',
        email:row?.email??'',
        cellPhone:row?.cellPhone??''
      }
    );
    setContactIdFromRow(row.id);
  }, []);

  const existingPhoneNumber = useCallback((coAplicant: NContact.CoApplicant) => {
    const priorityOrder: (keyof NContact.CoApplicant)[] = ['cellPhone', 'homePhone', 'workPhone'];

    for (const phoneField of priorityOrder) {
      const phoneNumber = coAplicant[phoneField];
      if (phoneNumber) {
        return phoneNumber;
      }
    }

    return '';
  }, []);

  return (
    <>
      <Box marginBottom="10px">
        <SectionTitle title="Co-Applicants" />

        <Table>
          <TableBody>
            {data.map((record) => (
              <CoApplicantTableRow key={record.id}>
                <CoApplicantTableCell
                  size="small"
                  sx={{ flex: 2.5 }}
                >
                  <CoApplicantName
                    onClick={() => {
                      onEdit?.(record.id);
                      setCoApplicantModalPosition?.((prevState) => prevState + 1);
                    }}
                  >
                    <GridCell text={`${record.firstName} ${record.lastName}`} />
                  </CoApplicantName>
                </CoApplicantTableCell>
                <CoApplicantTableCell
                  size="small"
                  sx={{ flex: 1.5 }}
                >
                  ID: {record.numericId}
                </CoApplicantTableCell>
                <CoApplicantTableCell
                  size="small"
                  sx={{ flex: 1.5 }}
                >
                  Phone: <PhoneWrapper onClick={(e) => handleClick(e, record)}>{existingPhoneNumber(record)}</PhoneWrapper>
                </CoApplicantTableCell>
                <CoApplicantTableCell
                  size="small"
                  sx={{ flex: 2 }}
                >
                  Email: <LinkButton href={'mailto:' + record.email}>{record.email}</LinkButton>
                </CoApplicantTableCell>
                <CoApplicantTableCell
                  size="small"
                  align="right"
                  sx={{ flex: 2 }}
                >
                  Combined DTI: {jointDtiResponse?.jointGTI?.jointDTI ? formatPercentage(jointDtiResponse?.jointGTI?.jointDTI) : '--'}
                </CoApplicantTableCell>
                <CoApplicantTableCell
                  align="right"
                  size="small"
                >
                  <Button
                    variant="text"
                    onClick={() => onRemove(record.id)}
                  >
                    Remove
                  </Button>
                </CoApplicantTableCell>
              </CoApplicantTableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Modal
        hideBackdrop
        title="SMS History"
        dialogHeight="800px"
        dialogWidth="530px"
        open={!!resetPosition}
        resetPosition={resetPosition}
        onClose={() => setResetPositon(0)}
      >
        {phoneNumber && (
          <ContactChatModal
            contactNumber={phoneNumber}
            contactId={contactIdFromRow}
            contactDetails={contactDetails}
          />
        )}
      </Modal>
    </>
  );
};
